<template>
  <div class="custom-v-dialog firmwide-file-selector">
    <v-dialog
      v-model="dialog"
      width="95vw"
      max-width="1100px"
      class="odaberite-spis-wrapper"
      content-class="file-selector-modal-redesigned"
    >
      <div class="file-selector-redesigned">
        <!-- Modal Header -->
        <div class="fs-header">
          <div class="fs-header-content">
            <div class="fs-header-icon">
              <v-icon size="22">mdi-file-document-multiple-outline</v-icon>
            </div>
            <div class="fs-header-text">
              <h2 class="fs-title">{{ $t('components.general.file_management.file_selector.document_selection') }}</h2>
              <span class="fs-subtitle">{{ $t('components.general.file_management.file_selector.select_subtitle') }}</span>
            </div>
          </div>
          <button class="fs-close-btn" @click="closeDialog()">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>

        <!-- Main Content Area -->
        <div class="fs-body">
          <!-- Left Column: Folder Picker -->
          <div class="fs-column fs-column--picker">
            <div class="fs-section-header">
              <v-icon size="18">mdi-folder-search-outline</v-icon>
              <span>{{ $t('components.general.file_management.file_selector.browse_folders') }}</span>
            </div>
            <div id="fw-file-selector" class="fs-picker-container">
              <firmwide-folder-picker @foldeSelected="updateSelectedFolder" />
            </div>
          </div>

          <!-- Right Column: Files List -->
          <div class="fs-column fs-column--files">
            <div class="fs-section-header">
              <v-icon size="18">mdi-file-multiple-outline</v-icon>
              <span>{{ $t('components.general.file_management.file_selector.available_files_title') }}</span>
              <span v-if="file_list.length > 0" class="fs-count-badge">{{ file_list.length }}</span>
            </div>

            <div class="fs-files-container">
              <!-- No Folder Selected State -->
              <div v-if="!folder_id" class="fs-empty-state">
                <div class="fs-empty-icon">
                  <v-icon size="48" color="#c4c4d4">mdi-folder-arrow-left-outline</v-icon>
                </div>
                <span class="fs-empty-title">{{ $t('components.general.file_management.file_selector.select_folder_first') }}</span>
                <span class="fs-empty-hint">{{ $t('components.general.file_management.file_selector.select_folder_hint') }}</span>
              </div>

              <!-- Loading State -->
              <div v-else-if="loadingFiles" class="fs-loading-state">
                <div class="fs-spinner">
                  <svg viewBox="0 0 50 50">
                    <circle cx="25" cy="25" r="20" fill="none" stroke-width="4"></circle>
                  </svg>
                </div>
                <span>{{ $t('components.general.file_management.file_selector.loading_files') }}</span>
              </div>

              <!-- Error State -->
              <div v-else-if="filesError" class="fs-error-state">
                <v-icon size="36" color="#c62828">mdi-alert-circle-outline</v-icon>
                <span>{{ filesError }}</span>
                <button class="fs-retry-btn" @click="getFiles()">
                  <v-icon size="14" left>mdi-refresh</v-icon>
                  {{ $t('components.general.file_management.file_selector.retry') }}
                </button>
              </div>

              <!-- Empty Folder State -->
              <div v-else-if="file_list.length === 0" class="fs-empty-state">
                <div class="fs-empty-icon">
                  <v-icon size="48" color="#c4c4d4">mdi-folder-open-outline</v-icon>
                </div>
                <span class="fs-empty-title">{{ $t('components.general.file_management.file_selector.empty_folder') }}</span>
              </div>

              <!-- Files List -->
              <div v-else class="fs-files-list">
                <div
                  v-for="(file, index) in file_list"
                  :key="file.id || file.name + index"
                  :class="['fs-file-item', { 'fs-file-item--selected': isFileSelected(file) }]"
                  @click="selectFile(file)"
                >
                  <div class="fs-file-icon">
                    <img :src="getFileIcon(file.name)" :alt="file.name" />
                    <transition name="fs-check">
                      <div v-if="isFileSelected(file)" class="fs-file-check">
                        <v-icon size="12" color="#fff">mdi-check</v-icon>
                      </div>
                    </transition>
                  </div>
                  <div class="fs-file-info">
                    <span class="fs-file-name" :title="file.name">{{ file.name }}</span>
                    <span class="fs-file-date">{{ formatDate(file.fileSystemInfo && file.fileSystemInfo.lastModifiedDateTime) }}</span>
                  </div>
                  <div class="fs-file-action">
                    <v-icon v-if="!isFileSelected(file)" size="18" color="#7b7b8a">mdi-plus-circle-outline</v-icon>
                    <v-icon v-else size="18" color="#0ACAB1">mdi-check-circle</v-icon>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Selected Files Bar -->
        <div class="fs-selection-bar" :class="{ 'fs-selection-bar--has-files': selected_file_list.length > 0 }">
          <div class="fs-selection-label">
            <v-icon size="18" :color="selected_file_list.length > 0 ? '#0ACAB1' : '#7b7b8a'">
              {{ selected_file_list.length > 0 ? 'mdi-check-circle' : 'mdi-checkbox-blank-circle-outline' }}
            </v-icon>
            <span>
              {{ selected_file_list.length > 0
                ? $t('components.general.file_management.file_selector.selected_files', { count: selected_file_list.length })
                : $t('components.general.file_management.file_selector.no_files_selected')
              }}
            </span>
          </div>

          <!-- Selected Files Chips -->
          <div class="fs-selection-chips" v-if="selected_file_list.length > 0">
            <transition-group name="fs-chip" tag="div" class="fs-chips-container">
              <div
                v-for="file in selected_file_list.slice(0, maxVisibleChips)"
                :key="file.id || file.name"
                class="fs-chip"
              >
                <img :src="getFileIcon(file.name)" class="fs-chip-icon" />
                <span class="fs-chip-name" :title="file.name">{{ truncateFileName(file.name) }}</span>
                <button class="fs-chip-remove" @click.stop="removeSelectedFile(file)">
                  <v-icon size="14">mdi-close</v-icon>
                </button>
              </div>
            </transition-group>
            <div v-if="selected_file_list.length > maxVisibleChips" class="fs-more-badge" @click="showAllSelected = !showAllSelected">
              +{{ selected_file_list.length - maxVisibleChips }} {{ $t('components.general.file_management.file_selector.more') }}
            </div>
          </div>

          <!-- Clear All Button -->
          <button
            v-if="selected_file_list.length > 1"
            class="fs-clear-all-btn"
            @click="clearAllSelected()"
          >
            {{ $t('components.general.file_management.file_selector.clear_all') }}
          </button>
        </div>

        <!-- Expanded Selected Files (when more than maxVisibleChips) -->
        <transition name="fs-expand">
          <div v-if="showAllSelected && selected_file_list.length > maxVisibleChips" class="fs-expanded-selection">
            <div class="fs-expanded-header">
              <span>{{ $t('components.general.file_management.file_selector.all_selected_files') }}</span>
              <button class="fs-collapse-btn" @click="showAllSelected = false">
                <v-icon size="16">mdi-chevron-up</v-icon>
              </button>
            </div>
            <div class="fs-expanded-chips">
              <div
                v-for="file in selected_file_list"
                :key="file.id || file.name + '-expanded'"
                class="fs-chip fs-chip--expanded"
              >
                <img :src="getFileIcon(file.name)" class="fs-chip-icon" />
                <span class="fs-chip-name" :title="file.name">{{ file.name }}</span>
                <button class="fs-chip-remove" @click.stop="removeSelectedFile(file)">
                  <v-icon size="14">mdi-close</v-icon>
                </button>
              </div>
            </div>
          </div>
        </transition>

        <!-- Footer Actions -->
        <div class="fs-footer">
          <button class="fs-btn fs-btn--secondary" @click="closeDialog()">
            {{ $t('components.general.file_management.file_selector.cancel') }}
          </button>
          <button
            class="fs-btn fs-btn--primary"
            :disabled="selected_file_list.length === 0"
            @click="acceptAllChanges()"
          >
            <v-icon size="18" left>mdi-check</v-icon>
            {{ $t('components.general.file_management.file_selector.confirm_selection') }}
            <span v-if="selected_file_list.length > 0" class="fs-btn-count">({{ selected_file_list.length }})</span>
          </button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import FirmwideFolderPicker from "./FirmwideFolderPicker.vue";
import { returnFileTypeImage } from "../../../helpers/file";
import graph from "@/services/graph.js";
import $ from "jquery";
import moment from "moment";

export default {
  components: { FirmwideFolderPicker },
  props: {
    message: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      dialog: false,
      folder_id: null,
      case_id: null,
      file_list: [],
      selected_file_list: [],
      loadingFiles: false,
      filesError: null,
      showAllSelected: false,
      maxVisibleChips: 4,
    };
  },
  methods: {
    truncateFileName(name, maxLength = 20) {
      if (!name || name.length <= maxLength) return name;
      const ext = name.lastIndexOf('.') > 0 ? name.substring(name.lastIndexOf('.')) : '';
      const baseName = name.substring(0, name.length - ext.length);
      const truncatedBase = baseName.substring(0, maxLength - ext.length - 3);
      return `${truncatedBase}...${ext}`;
    },

    clearAllSelected() {
      this.selected_file_list = [];
      this.showAllSelected = false;
    },

    closeDialog() {
      this.dialog = false;
      this.resetState();
    },

    resetState() {
      this.folder_id = null;
      this.case_id = null;
      this.file_list = [];
      this.filesError = null;
    },

    acceptAllChanges() {
      this.$emit("acceptChanges", this.selected_file_list);
      this.closeDialog();
    },

    formatDate(dt) {
      if (!dt) return "";
      return moment(dt).format("DD.MM.yyyy.");
    },

    isFileSelected(sel_file) {
      return this.selected_file_list.some((p) => p.id === sel_file.id);
    },

    removeSelectedFile(sel_file) {
      this.selected_file_list = this.selected_file_list.filter(
        (p) => p.id !== sel_file.id
      );
    },

    selectFile(sel_file) {
      if (!this.isFileSelected(sel_file)) {
        this.selected_file_list.unshift(sel_file);
      }
    },

    async getFiles() {
      if (!this.case_id || !this.folder_id) return;

      this.loadingFiles = true;
      this.filesError = null;
      this.file_list = [];

      try {
        const response = await graph.getFileChildren(
          this.case_id,
          this.folder_id
        );
        if (response.value && response.value.length > 0) {
          this.file_list = response.value.filter((p) => !p.folder);
        }
      } catch (error) {
        console.error("Error fetching files:", error);
        this.filesError = this.$t('components.general.file_management.file_selector.files_error');
      } finally {
        this.loadingFiles = false;
      }
    },

    updateSelectedFolder(selected_data) {
      if (
        this.folder_id !== selected_data.folder_id ||
        this.case_id !== selected_data.case_id
      ) {
        this.folder_id = selected_data.folder_id;
        this.case_id = selected_data.case_id;
        this.getFiles();
      }
    },

    getFileIcon(filename) {
      return returnFileTypeImage(filename);
    },

    applyDialogStyling() {
      this.$nextTick(() => {
        const element = document.getElementById("fw-file-selector");
        if (!element || !element.parentElement) return;

        $(element.parentElement.parentElement).addClass("file-upload-dialog-wrapper");
        $(element.parentElement).addClass("main-file-upload-wrapper");
        $(".spis-megawrapper").removeClass("active");
        $(".main-file-upload-wrapper").find(".spis-megawrapper:first").click();
      });
    },

    openDialog(prev_files = []) {
      this.selected_file_list = [...prev_files];
      this.dialog = true;
      this.applyDialogStyling();
    },
  },
};
</script>

<style></style>
